import { ROTULO_DO_TIPO_DE_MIDIA } from '@/shared/constants';
import { chaveDoDiaEmSaoPaulo } from '@/shared/formatters';
import { isRecord } from '@/shared/utils/apiResponse';
import { salvarBlobComoArquivo } from '@/shared/utils/browserActions';

import type {
  ContextoDaExportacao,
  EventoSimulacao,
  FormatoDaExportacao,
  ItemDaExportacao,
  SimulacaoInteraction,
  SimulacaoLink,
  SimulacaoMessage,
} from '../types';
import { mensagemDoEvento } from './mensagensDaConversa';
import { dataHoraDoEvento } from './rotulosDaSimulacao';

const TITULO = 'Simulação com a Lexi';
const AVISO = 'Nenhuma mensagem desta simulação foi enviada pelo WhatsApp.';
const PARTICIPANTE = 'Participante: você, no papel de cliente';

const disponibilidade = (disponivel: boolean) => (disponivel ? 'disponível' : 'indisponível');

/**
 * O destino só aparece quando difere do rótulo (link no texto já é a própria URL). A assinatura
 * fica sem destino: é rota interna da plataforma, não serve fora dela.
 */
const linhaDoLink = (prefixo: string, link: SimulacaoLink, comDestino = true): string => {
  if (!link.target) return `${prefixo}: ${link.label} (indisponível)`;
  if (!link.target.safe) return `${prefixo}: ${link.label} (indisponível: link inseguro)`;
  const destino = comDestino && link.target.href !== link.label ? ` <${link.target.href}>` : '';
  return `${prefixo}: ${link.label}${destino} (disponível)`;
};

const linhasDaInteracao = (interacao: SimulacaoInteraction): string[] => [
  ...(interacao.options ?? []).map((opcao) => `Opção: ${opcao.label}`),
  ...(interacao.sections ?? []).flatMap((secao) => [
    ...(secao.title ? [`Lista: ${secao.title}`] : []),
    ...secao.options.map((opcao) => `Opção: ${opcao.label}`),
  ]),
  ...(interacao.fields ?? []).map(
    (campo) => `Campo: ${campo.label}${campo.required ? ' (obrigatório)' : ''}`,
  ),
  ...(interacao.urlButton ? [linhaDoLink('Botão de link', interacao.urlButton)] : []),
];

const chaveDaOpcao = (mensagemId: string, opcaoId: string) => JSON.stringify([mensagemId, opcaoId]);

/** Rótulo de cada opção oferecida, para a escolha do cliente sair pelo nome e não pelo id. */
const opcoesOferecidas = (mensagens: SimulacaoMessage[]): Map<string, string> => {
  const opcoes = new Map<string, string>();
  for (const mensagem of mensagens) {
    if (mensagem.kind !== 'interactive') continue;
    const { messageId, options = [], sections = [] } = mensagem.interaction;
    for (const opcao of [...options, ...sections.flatMap((secao) => secao.options)]) {
      opcoes.set(chaveDaOpcao(messageId, opcao.id), opcao.label);
    }
  }
  return opcoes;
};

const escolhaDoCliente = (evento: EventoSimulacao, opcoes: Map<string, string>): string[] => {
  const resposta = evento.autor === 'CLIENTE' ? evento.payload.respostaDe : null;
  if (!isRecord(resposta)) return [];
  const { mensagemId, opcaoId } = resposta;
  if (typeof mensagemId !== 'string' || typeof opcaoId !== 'string') return [];
  return [`Escolha: ${opcoes.get(chaveDaOpcao(mensagemId, opcaoId)) ?? opcaoId}`];
};

const nomeDoTemplate = (evento: EventoSimulacao): string => {
  const { nomeTemplate } = evento.payload;
  return typeof nomeTemplate === 'string' && nomeTemplate ? `: ${nomeTemplate}` : '';
};

/** Só o assunto e a contagem: os endereços nem chegam ao evento. */
const linhasDoEmail = (evento: EventoSimulacao): string[] => {
  if (evento.tipo !== 'EMAIL') return [];
  const { assunto, quantidadeDestinatarios } = evento.payload;
  return [
    ...(typeof assunto === 'string' && assunto.trim() ? [`Assunto: ${assunto.trim()}`] : []),
    ...(typeof quantidadeDestinatarios === 'number'
      ? [`Destinatários: ${quantidadeDestinatarios}`]
      : []),
  ];
};

/** Sem URL de arquivo: a de acesso vale 60 s e é credencial (CA-18, CA-21). */
const linhasDoConteudo = (
  mensagem: SimulacaoMessage,
  evento: EventoSimulacao,
  contexto: ContextoDaExportacao,
): string[] => {
  switch (mensagem.kind) {
    case 'session':
      return [`Evento: ${mensagem.content}`, ...linhasDoEmail(evento)];
    case 'template':
      return [
        `Template WhatsApp${nomeDoTemplate(evento)}`,
        mensagem.content,
        ...mensagem.links.map((link) => linhaDoLink('Link', link)),
      ];
    case 'text':
      return [mensagem.content, ...mensagem.links.map((link) => linhaDoLink('Link', link))];
    case 'interactive':
      return [mensagem.content, ...linhasDaInteracao(mensagem.interaction)];
    case 'file': {
      const { fileId, mediaType, name } = mensagem.file;
      const disponivel = fileId !== null && !contexto.arquivosIndisponiveis.includes(fileId);
      return [
        mensagem.content,
        `${ROTULO_DO_TIPO_DE_MIDIA[mediaType]}: ${name} (${disponibilidade(disponivel)})`,
      ];
    }
    case 'signature':
      return [linhaDoLink('Assinatura de teste', mensagem.link, false)];
    case 'unknown':
      return [mensagem.content];
  }
};

/**
 * O histórico inteiro da simulação, em ordem de seq, inclusive o que o Limpar escondeu (CA-21,
 * CA-22): cada evento com autor (CA-8), data e hora `dd/MM/yyyy HH:mm` em Brasília e estado.
 */
export const itensDaExportacao = (
  eventos: EventoSimulacao[],
  contexto: ContextoDaExportacao,
): ItemDaExportacao[] => {
  const emOrdem = [...eventos].sort((a, b) => a.seq - b.seq);
  const mensagens = emOrdem.map((evento) => mensagemDoEvento(evento));
  const opcoes = opcoesOferecidas(mensagens);
  return emOrdem.map((evento, indice) => {
    const mensagem = mensagens[indice];
    return {
      cabecalho: [mensagem.author, dataHoraDoEvento(mensagem.createdAt), mensagem.status]
        .filter(Boolean)
        .join(' · '),
      linhas: [
        ...linhasDoConteudo(mensagem, evento, contexto),
        ...escolhaDoCliente(evento, opcoes),
      ].filter(Boolean),
    };
  });
};

const INLINE_DO_MARKDOWN = /[\\`*_[\]<>|~]/g;
const MARCADOR_NO_INICIO = /^(\s*)([#>+-])/;
const ITEM_NUMERADO_NO_INICIO = /^(\s*)(\d+)([.)])/;

/** Texto de agente e de cliente é livre: `# título` ou `<b>` nele não pode virar estrutura. */
export const escaparMarkdown = (texto: string): string =>
  texto
    .split('\n')
    .map((linha) =>
      linha
        .replace(INLINE_DO_MARKDOWN, '\\$&')
        .replace(MARCADOR_NO_INICIO, '$1\\$2')
        .replace(ITEM_NUMERADO_NO_INICIO, '$1$2\\$3'),
    )
    .join('\n');

/** Quebra de linha dura do Markdown: o texto da conversa mantém as linhas como foram escritas. */
const paragrafo = (texto: string) => escaparMarkdown(texto).split('\n').join('  \n');

export const conversaEmMarkdown = (
  eventos: EventoSimulacao[],
  contexto: ContextoDaExportacao,
): string =>
  [
    `# ${TITULO}`,
    '',
    `Agente: ${escaparMarkdown(contexto.agenteNome)}  `,
    ...(contexto.linhasDoAdmin ?? []).map((linha) => `${escaparMarkdown(linha)}  `),
    `${PARTICIPANTE}  `,
    AVISO,
    '',
    ...itensDaExportacao(eventos, contexto).flatMap((item) => [
      `## ${escaparMarkdown(item.cabecalho)}`,
      '',
      ...item.linhas.map(paragrafo).flatMap((linha) => [linha, '']),
    ]),
  ].join('\n');

const TROCAS_PARA_O_PDF: Record<string, string> = {
  '“': '"',
  '”': '"',
  '„': '"',
  '‘': "'",
  '’': "'",
  '‚': "'",
  '–': '-',
  '—': '-',
  '…': '...',
  '•': '*',
  '€': 'EUR',
};
const TABULACAO = 0x09;
const ehControle = (codigo: number) =>
  (codigo < 0x20 && codigo !== TABULACAO) || (codigo >= 0x7f && codigo <= 0x9f);

/**
 * A fonte padrão do jsPDF só cobre Latin-1: um único caractere fora dela (emoji, aspas curvas)
 * faz a linha inteira sair embaralhada. Acentos ficam; o resto vira o equivalente ou `?`. As
 * quebras de linha já foram separadas antes, então controle aqui é só lixo.
 */
export const textoParaPdf = (texto: string): string =>
  Array.from(texto, (caractere) => {
    const troca = TROCAS_PARA_O_PDF[caractere];
    if (troca !== undefined) return troca;
    const codigo = caractere.codePointAt(0) ?? 0;
    if (ehControle(codigo)) return '';
    return codigo > 0xff ? '?' : caractere;
  }).join('');

const nomeDoArquivo = (formato: FormatoDaExportacao) =>
  `simulacao-lexi-${chaveDoDiaEmSaoPaulo(new Date()) ?? 'conversa'}.${formato}`;

export const downloadConversationMd = (
  eventos: EventoSimulacao[],
  contexto: ContextoDaExportacao,
) => {
  const conteudo = conversaEmMarkdown(eventos, contexto);
  salvarBlobComoArquivo(
    new Blob([conteudo], { type: 'text/markdown;charset=utf-8' }),
    nomeDoArquivo('md'),
  );
};

const MARGEM_MM = 18;
const ALTURA_DA_LINHA_MM = 6;

/** jspdf é lib pesada: só carrega no clique de baixar. */
export const downloadConversationPdf = async (
  eventos: EventoSimulacao[],
  contexto: ContextoDaExportacao,
) => {
  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const alturaDaPagina = pdf.internal.pageSize.getHeight();
  const larguraDoTexto = pdf.internal.pageSize.getWidth() - MARGEM_MM * 2;
  let y = 20;

  const escrever = (texto: string, negrito = false) => {
    pdf.setFont('helvetica', negrito ? 'bold' : 'normal');
    const linhas = texto
      .split('\n')
      .flatMap((paragrafo) =>
        pdf.splitTextToSize(textoParaPdf(paragrafo) || ' ', larguraDoTexto),
      ) as string[];
    for (const linha of linhas) {
      if (y > alturaDaPagina - MARGEM_MM) {
        pdf.addPage();
        y = 20;
      }
      pdf.text(linha, MARGEM_MM, y);
      y += ALTURA_DA_LINHA_MM;
    }
  };

  pdf.setFontSize(15);
  escrever(TITULO, true);
  pdf.setFontSize(10);
  y += 2;
  escrever(`Agente: ${contexto.agenteNome}`);
  for (const linha of contexto.linhasDoAdmin ?? []) escrever(linha);
  escrever(PARTICIPANTE);
  escrever(AVISO);
  y += 5;
  for (const item of itensDaExportacao(eventos, contexto)) {
    escrever(item.cabecalho, true);
    for (const linha of item.linhas) escrever(linha);
    y += 4;
  }
  pdf.save(nomeDoArquivo('pdf'));
};
